<template>
  <RevCardCarousel
    v-if="shouldDisplayBlock"
    class="[&_li]:rounded-lg [&_li]:w-[223px] [&_li]:bg-[url('/img/product/verified-refurbished/fingerprint.svg'),_url('/img/product/verified-refurbished/holo.avif')] [&_li]:bg-blend-overlay [&_li]:[background-attachment:scroll,fixed] [&_li]:supports-[(-webkit-touch-callout:none)]:[background-attachment:scroll]"
    :next-alternative-text="i18n(translations.nextSlideText)"
    :prev-alternative-text="i18n(translations.prevSlideText)"
    :subtitle
    :title
  >
    <Card
      v-for="(checkpoint, index) in categories?.checkpoints"
      :key="checkpoint.text.id"
      :checkpoint
      :index
    />
  </RevCardCarousel>
  <ClientOnly>
    <VerifiedRefurbishedModal :link>
      <WarrantyExclusion />
    </VerifiedRefurbishedModal>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { categories as CHECKPOINTS_CATEGORIES_DATA } from '@backmarket/nuxt-layer-cms/BaseAnimatedProductCheckpoints/categories'
import VerifiedRefurbishedModal from '@backmarket/nuxt-layer-product-modals/VerifiedRefurbishedModal.vue'
import WarrantyExclusion from '@backmarket/nuxt-layer-product-modals/WarrantyExclusion.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevCardCarousel } from '@ds/components/CardCarousel'

import { CMS } from '~/scopes/cms/routes-names'

import {
  EXPRESS_REPLACEMENT_CATEGORIES,
  ProductCategory,
} from '../../constants'
import { CHECKPOINTS_CATEGORIES_MAPPING } from '../CheckpointsCarousel/CheckpointsCarousel.constants'

import translations from './VerifiedRefurbishedCarousel.translations'
import Card from './components/Card/Card.vue'

const { countryCode } = useMarketplace().market
const config = useRuntimeConfig()
const i18n = useI18n()
const experiments = useExperiments()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const props = defineProps<{
  categoryId: string
}>()

const link = resolveLocalizedRoute({
  name: CMS.QUALITY,
})

const shouldDisplayNewTitles = computed(() => {
  return (
    experiments['experiment.ppExpressReplacement'] ===
      'withExpressReplacement' &&
    countryCode === MarketCountryCode.FR &&
    EXPRESS_REPLACEMENT_CATEGORIES.includes(props.categoryId)
  )
})

const categories = computed(() => {
  const checkpointsCategoriesMapping =
    CHECKPOINTS_CATEGORIES_MAPPING[props.categoryId]

  return checkpointsCategoriesMapping
    ? CHECKPOINTS_CATEGORIES_DATA[checkpointsCategoriesMapping]
    : null
})

const isSmartphone = computed(() => {
  return props.categoryId === ProductCategory.SMARTPHONE
})

const shouldDisplayBlock = computed(() => {
  return (
    categories.value &&
    categories?.value.checkpoints &&
    config.public.FF_ENABLE_VERIFIED_REFURBISHED_LINK_PP?.includes(countryCode)
  )
})

const title = computed(() => {
  if (isSmartphone.value && shouldDisplayNewTitles) {
    return i18n(translations.titleNew)
  }
  if (shouldDisplayNewTitles.value) {
    return i18n(translations.dynamicTitleNew, {
      nb_points: categories.value?.checkpoints?.length,
    })
  }

  return i18n(translations.title)
})

const subtitle = computed(() => {
  if (shouldDisplayNewTitles.value) {
    return i18n(translations.subtitleNew)
  }

  if (isSmartphone.value) {
    return i18n(translations.subtitle)
  }

  return i18n(translations.subtitleDynamic, {
    nb_points: categories.value?.checkpoints?.length,
  })
})
</script>
